import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentDate, parsePerformanceTime } from "../lib/formatTime";
import { getStageColorAndGlow } from "../services/dataService";
import { isFavorite, toggleFavorite } from "../services/favoritesService";
import type { Performance } from "../types";
import PerformanceTimeLabel from "./PerformanceTimeLabel";
import { SparkleStars } from "./SparkleStars";
import { FavoriteHeart } from "./ui/FavoriteHeart";

interface PerformanceCardProps {
  performance: Performance & {
    artist?: {
      id: string;
      name: string;
      qualifier?: string | null;
      collective?: string | null;
      description?: string;
      genre?: string | null;
    };
    stage?: {
      id: string;
      name: string;
      icon?: string;
    };
    day?: {
      date: string;
    };
  };
  highlightActive?: boolean;
}

export const PerformanceCard: React.FC<PerformanceCardProps> = ({
  performance,
  highlightActive,
}) => {
  const [isFavorited, setIsFavorited] = useState(isFavorite(performance.id));
  const [isHovered, setIsHovered] = useState(false);

  const tagline = [performance.artist?.qualifier, performance.artist?.collective]
    .filter(Boolean)
    .join(" / ");

  // Sync state if favorites list changes in local storage
  useEffect(() => {
    setIsFavorited(isFavorite(performance.id));
  }, [performance.id]);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newFavorited = !isFavorited;
    toggleFavorite(performance.id);
    setIsFavorited(newFavorited);
  };

  const nowTime = getCurrentDate().getTime();
  const isActive =
    highlightActive &&
    parsePerformanceTime(performance.startTime, performance.dayId).getTime() < nowTime &&
    parsePerformanceTime(performance.endTime, performance.dayId).getTime() > nowTime;
  const hasEnded = parsePerformanceTime(performance.endTime, performance.dayId).getTime() < nowTime;

  const stageProps = getStageColorAndGlow(performance.stageId);

  // If the event has ended, the stars and stage pill should be reset to grey
  const starColor = hasEnded ? "#555555" : stageProps.color;
  const starGlow = hasEnded ? "rgba(0, 0, 0, 0)" : stageProps.glow;
  const pillColor = hasEnded ? "#888888" : stageProps.color;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative flex items-stretch border rounded-r-2xl rounded-l-md bg-[#222222]/65 backdrop-blur-md transition-all duration-300 hover:bg-[#333333]/80 ${
        isActive || isHovered ? "" : "border-white/5"
      }`}
      style={{
        borderColor: isActive || isHovered ? stageProps.color : undefined,
        boxShadow: isActive
          ? isHovered
            ? `0 6px 25px ${stageProps.glow.replace("0.5", "0.85")}`
            : `0 0 25px ${stageProps.glow.replace("0.5", "0.75")}`
          : isHovered
            ? `0 6px 20px ${stageProps.glow.replace("0.5", "0.60")}`
            : undefined,
      }}
    >
      {/* Stage Color Left Accent Bar */}
      <div
        className="w-1 rounded-l-md flex-shrink-0 opacity-75 group-hover:opacity-100"
        style={{ backgroundColor: stageProps.color }}
      />

      <Link
        id={`performance-card-link-${performance.id}`}
        to={`/performance/${performance.id}`}
        className="block flex-1 group pl-4 py-3 pr-12 relative overflow-hidden"
        aria-label={performance.artist?.name || "Ismeretlen előadó"}
      >
        {/* Top Active Tags Row */}
        <div className="flex justify-between items-center mb-2.5">
          {/* Stage badge */}
          {performance.stage?.name && (
            <div className="flex items-center gap-1 mt-1">
              <span
                className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1.5"
                style={{
                  color: pillColor,
                  borderColor: `${pillColor}40`,
                  backgroundColor: `${pillColor}15`,
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: pillColor }} />
                {performance.stage.name}
              </span>
            </div>
          )}

          {isActive && (
            <span className="text-[10px] font-bold text-[#FF3B30] uppercase tracking-wider flex items-center gap-1.5 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-[#FF3B30] before:shadow-[0_0_5px_#FF3B30] before:animate-pulse">
              On Air
            </span>
          )}
        </div>

        <div className="flex flex-col pr-4">
          {/* Performance Title / Artist Name */}
          <h3
            className="font-['Unbounded'] text-[15px] font-bold text-text leading-snug tracking-tight uppercase transition-colors"
            style={isHovered ? { color: stageProps.color } : undefined}
          >
            {performance.artist?.name || "Ismeretlen előadó"}
          </h3>
          {tagline && (
            <p className="text-[10px] text-muted italic mt-0.5 leading-tight">({tagline})</p>
          )}

          {/* Time & Duration Label */}
          <div className="flex items-center gap-1.5 text-xs text-white/90 mt-1.5">
            <span className="font-semibold">
              <PerformanceTimeLabel performance={performance} full={false} />
            </span>
          </div>

          {/* Description Snippet */}
          {performance.description && (
            <p className="text-xs text-muted mt-2 line-clamp-2 leading-relaxed whitespace-pre-line">
              {performance.description}
            </p>
          )}
        </div>

        {/* Glowing Retro Sparkle Stars decoration */}
        <SparkleStars
          seed={performance.id}
          color={starColor}
          glowColor={starGlow}
          className="absolute right-3.5 top-1/2 -translate-y-1/2"
        />
      </Link>

      {/* Favorite Button (On absolute right of container outside the main Link click area) */}
      <FavoriteHeart
        id={`favorite-btn-${performance.id}`}
        active={isFavorited}
        onClick={handleFavoriteClick}
        className="w-12 flex-shrink-0 flex items-center justify-center rounded-r-2xl z-20"
        iconClassName="h-5.5 w-5.5"
        targetName={performance.artist?.name || "Esemény"}
      />
    </div>
  );
};

export default PerformanceCard;
